import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Send, Headset, UserRound, LifeBuoy, Mail, Info } from "lucide-react";
import { toast } from "sonner";

const CONTACTS = [
  {
    id: "it-support",
    name: "IT-Support",
    email: "it-support@rernondis.de",
    displayEmail: "it-support@remondis.de",
    icon: LifeBuoy,
    description: "Unterstützung bei Installation und technischen Problemen",
  },
  {
    id: "philipp-kempkes",
    name: "Philipp Kempkes",
    email: "philipp.kempkes@rernondis.de",
    displayEmail: "philipp.kempkes@remondis.de",
    icon: UserRound,
    description: "Konkrete technische Fragen oder Entwicklungsaufträge",
  },
  {
    id: "kundenservice",
    name: "Kundenservice",
    email: "kundenservice@rernondis.de",
    displayEmail: "kundenservice@remondis.de",
    icon: Headset,
    description: "Allgemeine Fragen und allgemeine Anliegen",
  },
];

export default function Contact() {
  const [recipientId, setRecipientId] = useState(CONTACTS[2].id);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");

  const recipient = CONTACTS.find((c) => c.id === recipientId) || CONTACTS[0];

  const onSend = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Bitte Name angeben.");
      return;
    }
    if (!message.trim()) {
      toast.error("Bitte Nachricht eingeben.");
      return;
    }
    const subject = `Kontaktanfrage von ${name.trim()} – REMONDIS Anlagen-Portal`;
    const body = `Hallo ${recipient.name},\n\n${message.trim()}\n\nMit freundlichen Grüßen\n${name.trim()}`;
    const url = `mailto:${recipient.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = url;
    toast.success(`E-Mail-Programm wird geöffnet — Empfänger: ${recipient.name}`);
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link
          to="/"
          className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900 mb-6"
          data-testid="contact-back-link"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Zurück zu den Updates
        </Link>

        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
          Kontakt
        </div>
        <h1
          className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mt-1"
          data-testid="contact-title"
        >
          Wie können wir helfen?
        </h1>

        {/* Hint */}
        <div
          className="mt-6 flex gap-3 rounded-lg border border-slate-200 bg-white p-4"
          data-testid="contact-hint"
        >
          <Info className="h-5 w-5 text-[#E2001A] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-slate-700 leading-relaxed">
            Bei allgemeinen Fragen wenden Sie sich an den <strong>Kundenservice</strong>. Bei
            Unterstützung für die Installation bitte an den <strong>IT-Support</strong> und bei
            konkreten technischen Fragen oder Entwicklungsaufträgen direkt an{" "}
            <strong>Philipp Kempkes</strong>.
          </p>
        </div>

        {/* Recipient picker */}
        <div className="mt-8">
          <Label className="text-slate-700">Empfänger auswählen</Label>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            {CONTACTS.map((c) => {
              const Icon = c.icon;
              const active = recipientId === c.id;
              return (
                <button
                  type="button"
                  key={c.id}
                  onClick={() => setRecipientId(c.id)}
                  className={`text-left p-4 rounded-lg border transition-all ${
                    active
                      ? "border-[#E2001A] bg-white shadow-sm ring-2 ring-[#E2001A]/15"
                      : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                  data-testid={`contact-recipient-${c.id}`}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className={`h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                        active ? "bg-[#E2001A] text-white" : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-heading font-semibold text-slate-900 truncate">
                        {c.name}
                      </div>
                      <a
                        href={`mailto:${c.email}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs text-slate-500 hover:text-[#E2001A] truncate block"
                      >
                        {c.displayEmail}
                      </a>
                    </div>
                  </div>
                  <p className="text-xs text-slate-600 mt-2 leading-relaxed">{c.description}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={onSend}
          className="mt-8 bg-white border border-slate-200 rounded-lg p-6 space-y-4"
          data-testid="contact-form"
        >
          <div className="space-y-2">
            <Label htmlFor="name" className="text-slate-700">
              Ihr Name *
            </Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Vor- und Nachname"
              className="border-slate-300"
              data-testid="contact-name-input"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message" className="text-slate-700">
              Ihre Nachricht *
            </Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Beschreiben Sie Ihr Anliegen…"
              rows={7}
              className="border-slate-300"
              data-testid="contact-message-input"
              required
            />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
            <div className="text-sm text-slate-600 inline-flex items-center gap-2">
              <Mail className="h-4 w-4 text-slate-400" />
              Wird an <strong className="text-slate-900">{recipient.name}</strong>
              <span className="text-slate-400">({recipient.displayEmail})</span>
            </div>
            <Button
              type="submit"
              className="bg-[#E2001A] hover:bg-[#C20015] text-white"
              data-testid="contact-send-button"
            >
              <Send className="h-4 w-4 mr-2" />
              Nachricht senden
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            Beim Senden wird Ihr E-Mail-Programm geöffnet, damit Sie die Nachricht abschicken können.
          </p>
        </form>
      </div>
    </div>
  );
}
